/** @jsx isolateComponent */

import { isolateComponent } from '@zapier/style-encapsulation';
import { useSendFullStoryEvent, useTrackEvent } from '@zapier/toolbox-tracking';

import { Plan, UpgradeReason, upgradeReasons } from '@zapier/upsells/src/types';

import { EmailSupportUpgradeNudge } from './EmailSupportUpgradeNudge';
import { MultistepUpgradeNudge } from './MultistepUpgradeNudge';
import { PathsUpgradeNudge } from './PathsUpgradeNudge';
import { PremiumAppLimitUpgradeNudge } from './PremiumAppLimitUpgradeNudge';
import { PremiumAppUpgradeNudge } from './PremiumAppUpgradeNudge';
import { TaskLimitLowUpgradeNudge } from './TaskLimitLowUpgradeNudge';
import { TaskLimitUpgradeNudge } from './TaskLimitUpgradeNudge';
import { ZapLimitUpgradeNudge } from './ZapLimitUpgradeNudge';

type Props = {
  appName?: string;
  canExtendTrial: boolean;
  destinationPlan: Plan;
  onClose: () => void;
  onExtendTrialClick: () => void;
  onUpgradeClick: () => void;
  upgradeReason: UpgradeReason;
  wasTriggeredByZapToggle: boolean;
};

export const UpgradeNudge = ({
  appName,
  canExtendTrial,
  destinationPlan,
  onClose,
  onExtendTrialClick,
  onUpgradeClick,
  upgradeReason,
  wasTriggeredByZapToggle,
}: Props) => {
  const eventProperties = {
    upgradeReason,
  };

  useSendFullStoryEvent('Viewed Upgrade Modal', eventProperties);
  useTrackEvent('revenue: viewed-upgrade-modal', eventProperties);

  switch (upgradeReason) {
    case upgradeReasons.emailSupport:
      return (
        <EmailSupportUpgradeNudge
          onClose={onClose}
          onUpgradeClick={onUpgradeClick}
        />
      );

    case upgradeReasons.multistep:
      return (
        <MultistepUpgradeNudge
          canExtendTrial={canExtendTrial}
          onClose={onClose}
          onExtendTrialClick={onExtendTrialClick}
          onUpgradeClick={onUpgradeClick}
        />
      );

    case upgradeReasons.paths:
      return (
        <PathsUpgradeNudge
          canExtendTrial={canExtendTrial}
          onClose={onClose}
          onExtendTrialClick={onExtendTrialClick}
          onUpgradeClick={onUpgradeClick}
          wasTriggeredByZapToggle={wasTriggeredByZapToggle}
        />
      );

    case upgradeReasons.premium:
      return (
        <PremiumAppUpgradeNudge
          appName={appName}
          canExtendTrial={canExtendTrial}
          onClose={onClose}
          onExtendTrialClick={onExtendTrialClick}
          onUpgradeClick={onUpgradeClick}
        />
      );

    case upgradeReasons.premiumLimit:
      return (
        <PremiumAppLimitUpgradeNudge
          appName={appName}
          canExtendTrial={canExtendTrial}
          onClose={onClose}
          onExtendTrialClick={onExtendTrialClick}
          onUpgradeClick={onUpgradeClick}
        />
      );

    case upgradeReasons.taskLimit:
      return <TaskLimitUpgradeNudge onClose={onClose} />;

    case upgradeReasons.taskLimitLow:
      return <TaskLimitLowUpgradeNudge onClose={onClose} />;

    case upgradeReasons.zapLimit:
      return (
        <ZapLimitUpgradeNudge
          canExtendTrial={canExtendTrial}
          destinationPlan={destinationPlan}
          onClose={onClose}
          onExtendTrialClick={onExtendTrialClick}
          onUpgradeClick={onUpgradeClick}
        />
      );

    default:
      console.error(
        `Inline upgrade: invalid upgrade reason \`${upgradeReason}\``
      );
      return null;
  }
};
